import { SortCode } from "../interfaces/SortCode";
import { SortCodeItem } from "../interfaces/SortCodeMap";
import { SortField } from "../interfaces/SortField";
import { UseGroup } from "../interfaces/UseGroup";
import { fetchDataStrict } from "../clients/api";
import { handleError } from "../errors";
import { Result } from "./types";
import { CacheHandle, defineCachedLookup, defineCachedResource, defineCachedResourceById } from "./helpers";

const fetchSortFieldById = async (sortFieldId: string): Promise<Result<SortField>> => {
  const request = {
    table: "sort_fields",
    id: sortFieldId,
  };
  try {
    const response = await fetchDataStrict(request);
    return { success: true, data: response?.data };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchSortFieldById" });
    return { success: false, error: err };
  }
};

const fetchApplicationSortFieldsUncached = async (): Promise<Result<SortField[]>> => {
  const request = {
    table: "sort_fields",
    params: { search: "project;NULL" },
  };
  try {
    const response = await fetchDataStrict(request);
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, {
      ...request,
      context: "fetchApplicationSortFields",
    });
    return { success: false, error: err };
  }
};

const fetchProjectSortFieldsUncached = async (projectId: string): Promise<Result<SortField[]>> => {
  const request = {
    table: "sort_fields",
    params: { search: `project;${projectId}` },
  };
  try {
    const response = await fetchDataStrict(request);
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchProjectSortFields" });
    return { success: false, error: err };
  }
};

const fetchSortCodesBySortFieldIdUncached = async (sortFieldId: string): Promise<Result<SortCode[]>> => {
  const request = {
    table: "sort_codes",
    params: {
      search: `sort_field;${sortFieldId}`,
    },
  };
  try {
    const response = await fetchDataStrict(request);
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchSortCodesBySortFieldId" });
    return { success: false, error: err };
  }
};

const fetchMFCodesUncached = async (): Promise<Result<SortCodeItem[]>> => {
  const request = {
    table: "setup",
    params: { search: "key;sort_codes:mf" },
  };
  try {
    const response = await fetchDataStrict(request);
    const mfCodes = response?.data?.[0]?.value || [];
    return { success: true, data: mfCodes };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchMFCodes" });
    return { success: false, error: err };
  }
};

const fetchUFCodesUncached = async (): Promise<Result<SortCodeItem[]>> => {
  const request = {
    table: "setup",
    params: { search: "key;sort_codes:uf" },
  };
  try {
    const response = await fetchDataStrict(request);
    const ufCodes = response?.data?.[0]?.value || [];
    return { success: true, data: ufCodes };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchUFCodes" });
    return { success: false, error: err };
  }
};

const fetchUseGroupsUncached = async (): Promise<Result<UseGroup[]>> => {
  try {
    const response = await fetchDataStrict({ table: "use_groups" });
    return { success: true, data: response?.data };
  } catch (error) {
    const err = handleError(error, { table: "use_groups", context: "fetchUseGroups" });
    return { success: false, error: err };
  }
};

const fetchEscalationData = async (): Promise<Result<any>> => {
  const request = {
    table: "setup",
    params: { search: "key;escalation:region" },
  };
  try {
    const response = await fetchDataStrict(request);
    const escalationData = response?.data?.[0]?.value;
    return { success: true, data: escalationData };
  } catch (error) {
    const err = handleError(error, { ...request, context: "fetchEscalationData" });
    return { success: false, error: err };
  }
};

const applicationSortFields = defineCachedLookup(fetchApplicationSortFieldsUncached);
const projectSortFields = defineCachedResource<string, SortField[]>({
  scopeKey: (projectId) => projectId,
  fetch: fetchProjectSortFieldsUncached,
});
const sortCodes = defineCachedResourceById(fetchSortCodesBySortFieldIdUncached);
const mfCodes = defineCachedLookup(fetchMFCodesUncached);
const ufCodes = defineCachedLookup(fetchUFCodesUncached);
const useGroups = defineCachedLookup(fetchUseGroupsUncached);

const fetchApplicationSortFields = applicationSortFields.get;
const fetchProjectSortFields = projectSortFields.get;
const fetchSortCodesBySortFieldId = sortCodes.get;
const fetchMFCodes = mfCodes.get;
const fetchUFCodes = ufCodes.get;
const fetchUseGroups = useGroups.get;

export const sortCaches: CacheHandle[] = [
  applicationSortFields,
  projectSortFields,
  sortCodes,
  mfCodes,
  ufCodes,
  useGroups,
];

export {
  fetchSortFieldById,
  fetchApplicationSortFields,
  fetchApplicationSortFieldsUncached,
  fetchProjectSortFields,
  fetchProjectSortFieldsUncached,
  fetchSortCodesBySortFieldId,
  fetchSortCodesBySortFieldIdUncached,
  fetchMFCodes,
  fetchMFCodesUncached,
  fetchUFCodes,
  fetchUFCodesUncached,
  fetchUseGroups,
  fetchEscalationData,
};
